import React, { useState, useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { InitialStateUIType, InitialStateUserType } from "../../typings/state";
import "../riasec/riasec-test.scss";
import { useBreadcrumbSetter } from "../../hooks/useBreadcrumbSetter";
import { powerSkillService } from "../../services/powerskill.service";
import {
  PowerSkillQuestion,
  PowerSkillAnswer,
  PowerSkillLastTestResponse,
} from "../../typings/powerskill";
import PowerSkillTestIntro from "../../components/PowerSkill/PowerSkillTestIntro";
import PowerSkillTestQuestion from "../../components/PowerSkill/PowerSkillTestQuestion";
import PowerSkillTestResult from "../../components/PowerSkill/PowerSkillTestResult";

interface PowerSkillTestProps extends RouteComponentProps {
  user: InitialStateUserType;
  UI: InitialStateUIType;
}

const PowerSkillTest: React.FC<PowerSkillTestProps> = () => {
  const [lastTest, setLastTest] = useState<PowerSkillLastTestResponse | null>(null);
  const [questions, setQuestions] = useState<PowerSkillQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const [testResult, setTestResult] = useState<PowerSkillLastTestResponse | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [showAnswersSummary, setShowAnswersSummary] = useState(false);

  const breadcrumbs = useMemo(
    () => [{ label: "PowerSkills Test", isLast: true }],
    []
  );

  useBreadcrumbSetter(breadcrumbs);

  useEffect(() => {
    loadLastTest();
  }, []);

  const loadLastTest = async () => {
    try {
      const response = await powerSkillService.getLastTest();
      if(response?.status){
        setLastTest(response as PowerSkillLastTestResponse);
      }
    } catch (error) {
      console.error("Error loading last test:", error);
    }
  };

  const startTest = async () => {
    setLoading(true);
    try {
      const response = await powerSkillService.getAllQuestions(false); // Do not randomize to keep A,B,C,D,E order
      setQuestions(response.data);
      setIsTestStarted(true);
      setCurrentQuestionIndex(0);
      setAnswers({});
    } catch (error) {
      console.error("Error loading questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId: number, answer: number) => {
    if (loading) return;

    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));

    setLoading(true);
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      }
      setLoading(false);
    }, 500);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const answersArray: PowerSkillAnswer[] = Object.entries(answers).map(
        ([questionId, answer]) => ({
          question_id: Number.parseInt(questionId, 10),
          answer,
        })
      );

      const response = await powerSkillService.submitTest({
        answers: answersArray,
      });
      setTestResult(response);
      setIsTestCompleted(true);
    } catch (error) {
      console.error("Error submitting test:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    setIsTestStarted(false);
    setIsTestCompleted(false);
    setTestResult(null);
    loadLastTest();
  };

  const handleViewLastTestDetail = () => {
    setTestResult(lastTest);
    setIsTestCompleted(true);
  };

  const handleToggleSummary = (show: boolean) => {
    setShowAnswersSummary(show);
  };

  if (isTestCompleted && testResult) {
    return (
      <PowerSkillTestResult
        testResult={testResult}
        onBackToHome={handleBackToHome}
      />
    );
  }

  if (isTestStarted && questions.length > 0) {
    return (
      <PowerSkillTestQuestion
        questions={questions}
        currentQuestionIndex={currentQuestionIndex}
        answers={answers}
        loading={loading}
        showAnswersSummary={showAnswersSummary}
        onAnswer={handleAnswer}
        onNext={handleNext}
        onPrev={handlePrev}
        onSubmit={handleSubmit}
        onToggleSummary={handleToggleSummary}
      />
    );
  }

  return (
    <PowerSkillTestIntro
      loading={loading}
      lastTest={lastTest}
      onStartTest={startTest}
      onViewLastTestDetail={handleViewLastTestDetail}
    />
  );
};

export default PowerSkillTest;
